import { useState } from 'react';
import { Zap, Calendar, BarChart3, Users } from 'lucide-react';
import type { CrmAlerts } from '../../../api';
import { CrmHelmsmanBar } from './CrmHelmsmanBar';
import { CrmWorkflowGuide } from './CrmWorkflowGuide';
import { AdminGlassDisclosure } from '../AdminGlassDisclosure';
import { CrmMyDayPanel } from './CrmMyDayPanel';
import { CrmIntakeQueue } from './CrmIntakeQueue';
import { CrmPipelineKanban } from './CrmPipelineKanban';
import { CrmPipelineStageList } from './CrmPipelineStageList';
import { CrmManagerFilterChips } from './CrmManagerFilterChips';
import { CrmInsightsStrip } from './CrmInsightsStrip';
import { useCrmIntake, useCrmMyDay } from '../../../hooks/crm/useCrmQueries';
import type { AdminManagerBrief } from '../../../api';
import type { CrmAccessParams } from '../../../crmAccess';
import { canCrmFullControl } from '../../../crmAccess';
import type { CrmOpenDeal, CrmOpenDealHandler } from './crmDealDeepLink';

type Props = {
  access: CrmAccessParams;
  role: string;
  crmFull: boolean;
  managers: AdminManagerBrief[];
  statsManagerId: number | null;
  statsUnassignedOnly: boolean;
  onSelectManager: (id: number | null, unassigned?: boolean) => void;
  initialOrderId?: number | null;
  initialLeadId?: number | null;
  onOpenDeal?: CrmOpenDealHandler;
  crmDepartment?: string | null;
  onOpenMeasurement?: (id: number) => void;
  onOpenClient360?: (userId: number) => void;
  onOpenMeasurementsForUser?: (userId: number) => void;
  hapticSelection: () => void;
  hapticSuccess: () => void;
  onOpenLead: (id: number) => void;
  onOpenOrder: (id: number) => void;
  crmAlerts?: CrmAlerts | null;
};

export function CrmManagerDesk({
  access,
  role: _role,
  crmFull,
  managers,
  statsManagerId,
  statsUnassignedOnly,
  onSelectManager,
  initialOrderId,
  initialLeadId,
  onOpenDeal,
  crmDepartment,
  onOpenMeasurement,
  onOpenClient360,
  onOpenMeasurementsForUser,
  hapticSelection,
  hapticSuccess,
  onOpenLead,
  onOpenOrder,
  crmAlerts = null,
}: Props) {
  const effectiveMgr = crmFull && !statsUnassignedOnly ? statsManagerId : null;
  const { data: intake } = useCrmIntake(effectiveMgr);
  const { data: myDay } = useCrmMyDay(effectiveMgr);
  const intakeCount = intake?.count ?? 0;
  const myDayBadge = (myDay?.overdue.length ?? 0) + (myDay?.due_today.length ?? 0);

  const [intakeOpen, setIntakeOpen] = useState(false);
  const [myDayOpen, setMyDayOpen] = useState(false);
  const [funnelOpen, setFunnelOpen] = useState(false);

  const openDeal =
    onOpenDeal ??
    ((deal: CrmOpenDeal) => {
      if (deal.kind === 'order') onOpenOrder(deal.id);
      else onOpenLead(deal.id);
    });

  const kanbanOnly = (
    <CrmPipelineKanban
      access={access}
      hapticSelection={hapticSelection}
      hapticSuccess={hapticSuccess}
      onOpenDeal={openDeal}
      managerId={effectiveMgr}
      crmDepartment={crmDepartment}
      showInsights={false}
      managers={managers}
      onNavigateMeasurement={onOpenMeasurement}
      onNavigateClient360={onOpenClient360}
      onOpenMeasurementsForUser={onOpenMeasurementsForUser}
    />
  );

  return (
    <div className="space-y-3">
      <div
        className="rounded-2xl px-4 py-3"
        style={{ background: 'rgba(99,179,237,0.08)', border: '1px solid rgba(99,179,237,0.25)' }}
      >
        <p className="text-sm font-bold" style={{ color: 'var(--text-1)' }}>
          Рабочий стол продаж
        </p>
        <ol className="text-[11px] mt-2 space-y-1.5 list-decimal pl-4 leading-relaxed" style={{ color: 'var(--text-3)' }}>
          <li>
            <span className="font-semibold" style={{ color: 'var(--text-2)' }}>Входящие</span> — возьмите лид в работу.
          </li>
          <li>
            <span className="font-semibold" style={{ color: 'var(--text-2)' }}>Мой день</span> — просрочки и задачи на сегодня.
          </li>
          <li>
            <span className="font-semibold" style={{ color: 'var(--text-2)' }}>Воронка</span> — этапы списком, без горизонтального скролла.
          </li>
        </ol>
      </div>

      <CrmHelmsmanBar
        alerts={crmAlerts}
        intakeCount={intakeCount}
        onFocusIntake={() => setIntakeOpen(true)}
        onFocusMyDay={() => setMyDayOpen(true)}
        onOpenOrder={(id) => openDeal({ kind: 'order', id })}
        hapticSelection={hapticSelection}
      />

      {crmFull && managers.length > 0 && (
        <CrmManagerFilterChips
          managers={managers}
          selectedId={statsUnassignedOnly ? null : statsManagerId}
          unassignedOnly={statsUnassignedOnly}
          showAll
          onSelect={onSelectManager}
          hapticSelection={hapticSelection}
        />
      )}

      <AdminGlassDisclosure
        accent="coral"
        open={intakeOpen}
        onOpenChange={setIntakeOpen}
        title="Входящие и лиды"
        subtitle="Telegram, замер, ручной ввод"
        icon={<Zap size={16} color="#FC814A" />}
        badge={intakeCount > 0 ? intakeCount : undefined}
      >
        <CrmIntakeQueue
          access={access}
          managerId={effectiveMgr}
          onOpenLead={(id) => openDeal({ kind: 'lead', id })}
          hapticSelection={hapticSelection}
          hapticSuccess={hapticSuccess}
        />
      </AdminGlassDisclosure>

      <AdminGlassDisclosure
        accent="cyan"
        open={myDayOpen}
        onOpenChange={setMyDayOpen}
        title="Мой день"
        subtitle="Задачи по дедлайну"
        icon={<Calendar size={16} color="#63B3ED" />}
        badge={myDayBadge > 0 ? myDayBadge : undefined}
      >
        <CrmMyDayPanel
          managerId={effectiveMgr}
          onOpenDeal={openDeal}
          hapticSelection={hapticSelection}
        />
      </AdminGlassDisclosure>

      <AdminGlassDisclosure
        accent="emerald"
        open={funnelOpen}
        onOpenChange={setFunnelOpen}
        title="Воронка продаж"
        subtitle="Этапы · поиск · экспорт"
        icon={<BarChart3 size={16} color="#68D391" />}
      >
        <CrmPipelineStageList
          access={access}
          hapticSelection={hapticSelection}
          hapticSuccess={hapticSuccess}
          initialOrderId={initialOrderId}
          initialLeadId={initialLeadId}
          onOpenDeal={openDeal}
          managerId={effectiveMgr}
          crmDepartment={crmDepartment}
          managers={managers}
          kanbanSlot={kanbanOnly}
        />
      </AdminGlassDisclosure>

      {crmFull && canCrmFullControl(access) && (
        <AdminGlassDisclosure
          accent="amber"
          defaultOpen={false}
          title="Команда"
          subtitle="Фильтр по менеджеру"
          icon={<Users size={16} color="#F6AD55" />}
        >
          <CrmManagerFilterChips
            managers={managers}
            selectedId={statsManagerId}
            unassignedOnly={statsUnassignedOnly}
            showAll
            onSelect={onSelectManager}
            hapticSelection={hapticSelection}
          />
          <div className="mt-3">
            <CrmInsightsStrip managerId={statsManagerId} days={7} />
          </div>
        </AdminGlassDisclosure>
      )}

      <CrmWorkflowGuide />
    </div>
  );
}
